/**
 * Sort keys — the order of a trip day's activities without renumbering.
 *
 * Written as CommonJS so the frontend can `import` it and the backend can
 * `require` it, from one file (same pattern as `durationParse.js`).
 *
 * Why: the itinerary's order used to be one trip-wide integer
 * (`trip_activities.display_order`) rewritten from the payload on every save,
 * so two people reordering at once could not both land — the later save
 * renumbered everything. A sort key is a string that sorts between its
 * neighbours: moving an activity writes ONE key, never its neighbours'
 * (docs/plans/TRIP_COEDITING_PLAN.md §4.3).
 *
 * The algorithm is the standard "fractional indexing" one (David Greenspan,
 * as published by rocicorp/fractional-indexing): a key is an integer part
 * (a head letter giving its length, then base-62 digits) plus an optional
 * fraction. Keys compare with plain string comparison — JS `<` and SQLite's
 * default BINARY collation agree, because every digit is ASCII and the digit
 * alphabet is in ASCII order.
 *
 * Two clients inserting between the same two keys get the SAME key; ties
 * break on activity_id wherever activities are ordered (ORDER BY sort_key,
 * activity_id).
 *
 * `keyForIndex(n)` is the key the n-th integer would get, so a list ordered
 * by an integer (the legacy `display_order`) maps to keys in the same order.
 * The migration's trigger computes the same key in SQL (`keyForIndexSql`),
 * and a test holds the two to each other.
 */

const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const ZERO = DIGITS[0];
const SMALLEST_INTEGER = 'A' + ZERO.repeat(26);

function integerLength(head) {
  if (head >= 'a' && head <= 'z') return head.charCodeAt(0) - 'a'.charCodeAt(0) + 2;
  if (head >= 'A' && head <= 'Z') return 'Z'.charCodeAt(0) - head.charCodeAt(0) + 2;
  throw new Error(`invalid sort key head: ${head}`);
}

function integerPart(key) {
  const length = integerLength(key[0]);
  if (length > key.length) throw new Error(`invalid sort key: ${key}`);
  return key.slice(0, length);
}

function validateInteger(int) {
  if (int.length !== integerLength(int[0])) throw new Error(`invalid sort key integer: ${int}`);
}

/** Whether `key` is a well-formed sort key. */
function isValidSortKey(key) {
  // Keys grow ~1 character per 6 inserts into the same gap; the cap only
  // stops a malformed or hostile value. (Rebalancing a day is the answer to
  // a very long key, not refusing it.)
  if (typeof key !== 'string' || key.length === 0 || key.length > 1024) return false;
  try {
    if (key === SMALLEST_INTEGER) return false;
    const int = integerPart(key);
    for (const ch of key.slice(1)) if (DIGITS.indexOf(ch) === -1) return false;
    const fraction = key.slice(int.length);
    return fraction.slice(-1) !== ZERO;
  } catch (_) {
    return false;
  }
}

function validateKey(key) {
  if (!isValidSortKey(key)) throw new Error(`invalid sort key: ${key}`);
}

// A fraction strictly between fractions a and b (b null = no upper bound).
function midpoint(a, b) {
  if (b !== null && a >= b) throw new Error(`${a} >= ${b}`);
  if (a.slice(-1) === ZERO || (b && b.slice(-1) === ZERO)) throw new Error('trailing zero');
  if (b) {
    let n = 0;
    while ((a[n] || ZERO) === b[n]) n++;
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
  }
  const digitA = a ? DIGITS.indexOf(a[0]) : 0;
  const digitB = b !== null ? DIGITS.indexOf(b[0]) : DIGITS.length;
  if (digitB - digitA > 1) return DIGITS[Math.round(0.5 * (digitA + digitB))];
  if (b && b.length > 1) return b.slice(0, 1);
  return DIGITS[digitA] + midpoint(a.slice(1), null);
}

function incrementInteger(x) {
  validateInteger(x);
  const [head, ...digs] = x.split('');
  let carry = true;
  for (let i = digs.length - 1; carry && i >= 0; i--) {
    const d = DIGITS.indexOf(digs[i]) + 1;
    if (d === DIGITS.length) {
      digs[i] = ZERO;
    } else {
      digs[i] = DIGITS[d];
      carry = false;
    }
  }
  if (!carry) return head + digs.join('');
  if (head === 'Z') return 'a' + ZERO;
  if (head === 'z') return null;
  const h = String.fromCharCode(head.charCodeAt(0) + 1);
  if (h > 'a') digs.push(ZERO); else digs.pop();
  return h + digs.join('');
}

function decrementInteger(x) {
  validateInteger(x);
  const [head, ...digs] = x.split('');
  let borrow = true;
  for (let i = digs.length - 1; borrow && i >= 0; i--) {
    const d = DIGITS.indexOf(digs[i]) - 1;
    if (d === -1) {
      digs[i] = DIGITS.slice(-1);
    } else {
      digs[i] = DIGITS[d];
      borrow = false;
    }
  }
  if (!borrow) return head + digs.join('');
  if (head === 'a') return 'Z' + DIGITS.slice(-1);
  if (head === 'A') return null;
  const h = String.fromCharCode(head.charCodeAt(0) - 1);
  if (h < 'Z') digs.push(DIGITS.slice(-1)); else digs.pop();
  return h + digs.join('');
}

/**
 * A key strictly between `a` and `b`. Either may be null: null `a` = before
 * everything, null `b` = after everything, both null = the first key.
 * Throws on malformed keys or a >= b (callers validate input first).
 */
function keyBetween(a, b) {
  if (a != null) validateKey(a);
  if (b != null) validateKey(b);
  if (a != null && b != null && a >= b) throw new Error(`${a} >= ${b}`);
  if (a == null) {
    if (b == null) return 'a' + ZERO;
    const ib = integerPart(b);
    const fb = b.slice(ib.length);
    if (ib === SMALLEST_INTEGER) return ib + midpoint('', fb);
    if (ib < b) return ib;
    const res = decrementInteger(ib);
    if (res === null) throw new Error('cannot decrement any more');
    return res;
  }
  if (b == null) {
    const ia = integerPart(a);
    const fa = a.slice(ia.length);
    const i = incrementInteger(ia);
    return i === null ? ia + midpoint(fa, null) : i;
  }
  const ia = integerPart(a);
  const fa = a.slice(ia.length);
  const ib = integerPart(b);
  const fb = b.slice(ib.length);
  if (ia === ib) return ia + midpoint(fa, fb);
  const i = incrementInteger(ia);
  if (i === null) throw new Error('cannot increment any more');
  if (i < b) return i;
  return ia + midpoint(fa, null);
}

// keyForIndex covers heads a/b/c: 62 one-digit keys (a0..az), then 3,844
// two-digit (b00..bzz), then 238,328 three-digit — the order keyBetween's
// increments walk. The legacy display_order never comes near the end.
// Larger (or negative / missing) values clamp.
const B_START = 62;
const C_START = 62 + 62 * 62;
const MAX_INDEX = C_START + 62 * 62 * 62 - 1;

/** The key of the n-th position in an integer-ordered list. */
function keyForIndex(n) {
  // As SQLite's CAST reads it (keyForIndexSql): '7' is 7.
  const num = typeof n === 'string' && n.trim() !== '' ? Number(n) : n;
  let v = Number.isFinite(num) ? Math.floor(num) : 0;
  v = Math.max(0, Math.min(MAX_INDEX, v));
  if (v < B_START) return 'a' + DIGITS[v];
  if (v < C_START) {
    const b = v - B_START;
    return 'b' + DIGITS[Math.floor(b / 62)] + DIGITS[b % 62];
  }
  const c = v - C_START;
  return 'c' + DIGITS[Math.floor(c / 3844)] + DIGITS[Math.floor(c / 62) % 62] + DIGITS[c % 62];
}

/**
 * SQL for keyForIndex(<expr>) — the same key, computed by SQLite (the
 * revision migration's trigger keeps legacy writers' rows keyed with it).
 * `expr` is trusted SQL (a column reference).
 */
function keyForIndexSql(expr) {
  const n = `MIN(${MAX_INDEX}, MAX(0, CAST(COALESCE(${expr}, 0) AS INTEGER)))`;
  const d = (x) => `substr('${DIGITS}', (${x}) + 1, 1)`;
  const b = `(${n} - ${B_START})`;
  const c = `(${n} - ${C_START})`;
  return `(CASE WHEN ${n} < ${B_START} THEN 'a' || ${d(n)}
    WHEN ${n} < ${C_START} THEN 'b' || ${d(`${b} / 62`)} || ${d(`${b} % 62`)}
    ELSE 'c' || ${d(`${c} / 3844`)} || ${d(`(${c} / 62) % 62`)} || ${d(`${c} % 62`)} END)`;
}

/** Compare two keyed items: by key, then by id (ties are possible). */
function compareKeyed(a, b) {
  if (a.key < b.key) return -1;
  if (a.key > b.key) return 1;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
}

module.exports = { keyBetween, keyForIndex, keyForIndexSql, isValidSortKey, compareKeyed, MAX_INDEX };
